import { has, get, find } from 'lodash';
import { getPayeeById } from '../commonSelectors/selectors';
import { getPaymentsWithAccounts } from '../accounts/selectors';

import { payeeTypes, TYPE_DOMESTIC_PAYEE, TYPE_IFT_PAYEE, TYPE_IFT } from '../../config/';
import { getPaymentTypeOrPayeeType } from '../modes/selectors';
import { getPayeeType } from '../../managePayee/states/selectors';

export const getFormattedPayees = (payee) => ({
	id: payee.id,
	singleUse: payee.singleUse,
	reference: payee.reference,
	displayName: get(payee, 'display_name', '') || get(payee, 'to_account.name', ''),
	iban: get(payee, 'to_account.iban'),
	accountNumber: get(payee, 'to_account.account_number'),
	sortCode: get(payee, 'to_account.sort_code'),
	isBiller: has(payee, 'biller_id'),
});

export const isPayee = (state, id) => state.payees.some((payee) => payee.id === id);

export const isSingleUsePayee = (state, id) =>
	state.payees.some((payee) => payee.id === id && get(payee, 'singleUse', false));

export const getPayees = (state) =>
	state.payees
		.filter((payee) => !payee.biller_id && payee.type !== payeeTypes.INTERNATIONAL)
		.map((payee) => ({
			...payee,
			...getFormattedPayees(payee),
		}));

export const getInternationalPayees = (state) =>
	state.payees
		.filter((payee) => !payee.biller_id && payee.type === payeeTypes.INTERNATIONAL)
		.map((payee) => ({
			...payee,
			...getFormattedPayees(payee),
		}));

export const getPaymentsList = (state, paymentType) => {
	if (paymentType === 'bacsCredit') {
		return state.bacsCredit.payments;
	}
	if (paymentType === 'bacsDebit') {
		return state.bacsDebit.payments;
	}
	return state.payments;
};

export const isPayingInternalAccountsWithoutBeneficiary = (state, paymentType) => {
	const payments = getPaymentsWithAccounts(state, getPaymentsList(state, paymentType));

	return payments.filter((payment) => {
		const toAccount = paymentType === 'bacsDebit' ? payment.fromAccount : payment.toAccount;
		return !(isPayee(state, toAccount) || payment.beneficiary_id);
	});
};

export const hasRefChangedFromDefault = (state, benAcc, reference) =>
	getPayeeById(state, benAcc) && get(getPayeeById(state, benAcc), 'reference') !== reference;

const getIFTDetail = (payee) => {
	if (payee.type === payeeTypes.INTERNATIONAL) {
		return {
			bic: get(payee, 'to_account.bic', ''),
			clearingCode: get(payee, 'to_account.clearing_code', ''),
		};
	}
	return {};
};

export const getManagePayeeList = (state) => {
	const payeeType = getPayeeType(state);
	return state.payees
		.map((payee) => {
			const type = payee.type === payeeTypes.INTERNATIONAL ? TYPE_IFT_PAYEE : TYPE_DOMESTIC_PAYEE;
			const payeeType = payee.type;

			return {
				...getFormattedPayees(payee),
				type,
				payeeType,
				...getIFTDetail(payee),
			};
		})
		.filter((payee) => !payee.isBiller && !payee.singleUse && payee.type === payeeType);
};

export const getPayeeIds = (state) => {
	const paymentTypeOrPayeeType = getPaymentTypeOrPayeeType(state);
	if (paymentTypeOrPayeeType === TYPE_IFT || paymentTypeOrPayeeType === TYPE_IFT_PAYEE) {
		return { addPayeeId: 'addInternationalPayee', editPayeeId: 'editInternationalPayee' };
	}
	return { addPayeeId: 'addPayee', editPayeeId: 'editPayee' };
};

export const getPayeeBySortCodeAccountNumber = (state, sortCode = '', accountNumber = '') =>
	find(
		state.payees,
		(payee) =>
			get(payee, 'to_account.sort_code') === sortCode && get(payee, 'to_account.account_number') === accountNumber
	);

export const getPayeeBySortCodeAccountNumberName = (state, sortCode = '', accountNumber = '', name = '') =>
	find(
		state.payees,
		(payee) =>
			get(payee, 'to_account.sort_code') === sortCode &&
			get(payee, 'to_account.account_number') === accountNumber &&
			get(payee, 'to_account.name') === name
	);

export const getPayeeByIBANOrAccountNumber = (state, iban = '', accountNumber = '') =>
	find(
		state.payees,
		(payee) => get(payee, 'to_account.iban') === iban || get(payee, 'to_account.account_number') === accountNumber
	);

export const getIsDuplicatePayeeSubmitted = (state) => {
	return get(state, 'api.duplicatePayeeSubmitted');
};

export const getAccountError = (state) => {
	return get(state, 'api.accountError');
};

export const getTemplateError = (state) => get(state, 'api.templateError');

export const getToPayeeCopCheckRequired = (state, toPayeeId) => {
	const payee = state.payees.find((payee) => payee.id === toPayeeId);
	return get(payee, 'isCopCheckRequired', false);
};
